'use client';

import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import RichText from '@/components/atoms/rich-text/rich-text';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import Button from '@/components/molecules/button/button';
import Input from '@/components/molecules/input/input';
import variables from '@/components/templates/my-trip-guest-login/my-trip-guest-login.variables';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import useMutationMyTripGuestLogin from '@/integrations/content/users/my-trip-guest-login/my-trip-guest-login.hooks';
import {
  trackPageLoadEvent,
  trackViewEvent,
} from '@/integrations/tracking/google-tag-manager/scripts';
import { tealiumView } from '@/integrations/tracking/tealium/scripts';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './my-trip-guest-login.module.scss';

export interface MyTripGuestLoginProps extends ModuleWrapperProps {
  /** The type of the page */
  $type: 'MyTripGuestLoginPageDto';
  /** The button text for login operation */
  buttonText?: string;
  /** The headline of the module */
  headline?: string;
  /** The help text to show at the bottom */
  helpText?: string | null;
  /** The text to show in the alert banner */
  noteText?: string | null;
  /** Personalization data for the user */
  personalizationData?: Content.Properties.PersonalizationData;
  /** The profile text */
  profileText?: string | null;
  /** The description of the module */
  text?: string;
  /** Tracking data for the page */
  trackingData?: Content.Properties.TrackingData;
}

interface FormValues {
  bookingNo: string;
  password: string;
}

const MyTripGuestLogin = ({
  buttonText,
  headline,
  helpText,
  noteText,
  profileText,
  text,
  personalizationData,
  trackingData,
  ...rest
}: MyTripGuestLoginProps) => {
  const { t } = useI18n();
  const router = useRouter();
  const [shouldShowAlert, setShouldShowAlert] = useState(true);

  const {
    data: dataMyTripGuestLogin,
    isLoading: isMutatingMyTripGuestLogin,
    mutateAsync: mutateMyTripGuestLogin,
  } = useMutationMyTripGuestLogin();

  const hasErrorMessage =
    dataMyTripGuestLogin &&
    !dataMyTripGuestLogin.url &&
    dataMyTripGuestLogin.message;

  const defaultValues = {
    bookingNo: '',
    password: '',
  };

  const { formState, getValues, register } = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: 'onChange',
  });

  const getErrorMessage = (fieldName: keyof FormValues): string | undefined => {
    const error = formState.errors[fieldName];

    if (!error) return;

    return error.message;
  };

  const handleMyTripGuestLogin = async () => {
    setShouldShowAlert(true);

    const values = getValues();

    await mutateMyTripGuestLogin({
      parameters: {
        bookingNo: values.bookingNo,
        password: values.password,
      },
    });
  };

  useEffect(() => {
    trackPageLoadEvent({ content_group: 'my_trip_guest_login' });

    personalizationData && tealiumView(personalizationData);

    if (!trackingData?.gtmDataLayer) return;

    trackViewEvent(trackingData.gtmDataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dataMyTripGuestLogin) return;

    if (!dataMyTripGuestLogin.url) return;

    router.push(dataMyTripGuestLogin.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMyTripGuestLogin]);

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container className={styles.container}>
        {hasErrorMessage && shouldShowAlert && (
          <Row>
            <Column
              offset={{ laptop: 2, tablet: 1 }}
              width={{ laptop: 8, tablet: 10 }}
            >
              {dataMyTripGuestLogin?.message && (
                <AlertBanner variant="attention">
                  <div className={styles.alertWrapper}>
                    <RichText text={dataMyTripGuestLogin.message} />

                    <Button
                      className={styles.alertButton}
                      icon="Close"
                      onClick={() => setShouldShowAlert(false)}
                      variant="ghost"
                    />
                  </div>
                </AlertBanner>
              )}
            </Column>
          </Row>
        )}

        <Row>
          <Column
            offset={{ laptop: 2, tablet: 1 }}
            width={{ laptop: 8, tablet: 10 }}
          >
            <div className={styles.card}>
              <div className={styles.header}>
                <Typography
                  className={styles.headline}
                  tagStyle="headingMedium"
                >
                  {headline}
                </Typography>

                {text && <RichText className={styles.text} text={text} />}
              </div>

              {isMutatingMyTripGuestLogin ? (
                <Spinner
                  className={styles.spinner}
                  hasTransparentOverlay={false}
                  loadingText={`${t('items.modules.bookingFlow.findingBooking')}...`}
                />
              ) : (
                <React.Fragment>
                  {noteText && (
                    <AlertBanner
                      variant={variables.noteTextAlertBanner.variant}
                    >
                      <RichText
                        text={noteText}
                        typographyVariant={
                          variables.noteTextAlertBanner.variant === 'highVis'
                            ? 'bodyMediumBold'
                            : undefined
                        }
                      />
                    </AlertBanner>
                  )}

                  <div className={styles.inputs}>
                    <Input
                      {...register('bookingNo', { required: true })}
                      className={styles.input}
                      error={getErrorMessage('bookingNo')}
                      id="bookingNo"
                      label={t('items.modules.bookingFlow.bookingNo')}
                      required
                    />

                    <Input
                      {...register('password', { required: true })}
                      className={styles.input}
                      error={getErrorMessage('password')}
                      id="password"
                      label={t('items.modules.bookingFlow.password')}
                      required
                    />
                  </div>

                  {profileText && <RichText text={profileText} />}

                  <Button
                    className={styles.button}
                    disabled={!formState.isValid}
                    onClick={() => handleMyTripGuestLogin()}
                    text={buttonText || t('items.modules.bookingFlow.login')}
                    variant="primary"
                  />
                </React.Fragment>
              )}
            </div>
          </Column>
        </Row>

        {helpText && (
          <Row>
            <Column
              offset={{ laptop: 2, tablet: 1 }}
              width={{ laptop: 8, tablet: 10 }}
            >
              <div className={styles.footer}>
                <RichText className={styles.helpText} text={helpText} />
              </div>
            </Column>
          </Row>
        )}
      </Container>
    </ModuleWrapper>
  );
};

MyTripGuestLogin.displayName = 'MyTripGuestLogin';

export default MyTripGuestLogin;
